// Demo-deployment mode — a SEPARATE build of the app for showing Boaz AI to
// prospects, running against its own demo database with only fictional data.
//
// How it works end to end:
//   * The demo site is a second deployment whose frontend is built with
//     VITE_DEMO_MODE=1 and whose backend runs with DEMO_MODE=true and a
//     DEMO_ACCESS_CODE, pointed at a dedicated demo database.
//   * There is NO Microsoft sign-in on the demo site. Instead every API call
//     carries the bearer token `demo:<access code>`; the backend (only when
//     DEMO_MODE=true) resolves that to a fixed "Demo Presenter" user with the
//     CEO role, so every screen is visible.
//   * The share link is  https://<demo-site>/?code=<access code> — the code is
//     captured from the URL into sessionStorage, so it survives navigation but
//     a visitor without the link sees an access-code prompt, not the app.
//
// This module is inert on the real production build (VITE_DEMO_MODE unset):
// IS_DEMO_BUILD is false and none of the auth shims take the demo branch.

const raw = String(import.meta.env.VITE_DEMO_MODE ?? '').toLowerCase();
export const IS_DEMO_BUILD = raw === '1' || raw === 'true';

const CODE_KEY = 'boaz_demo_access_code';

/** Capture ?code= from the URL (once) and return the stored access code. */
export function demoAccessCode(): string {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('code');
    if (fromUrl) sessionStorage.setItem(CODE_KEY, fromUrl);
    return sessionStorage.getItem(CODE_KEY) ?? '';
  } catch {
    return '';
  }
}

export function setDemoAccessCode(code: string): void {
  try { sessionStorage.setItem(CODE_KEY, code); } catch { /* private mode */ }
}

export function clearDemoAccessCode(): void {
  try { sessionStorage.removeItem(CODE_KEY); } catch { /* private mode */ }
}

/** Bearer token the demo backend recognises (see backend middleware/auth.ts). */
export function demoBearerToken(): string {
  const code = demoAccessCode();
  return code ? `demo:${code}` : 'demo';
}
